import { Play } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useProject, useProjectRuns } from "@/api/projects";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { FlowLaunchPanel, flowSubmitPath } from "@/components/projects/FlowLaunchPanel";
import { ProjectActions } from "@/components/projects/ProjectActions";
import { RunListAgGrid } from "@/components/runs/RunListAgGrid";
import { buttonVariants } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface RouteParams {
  slug?: string;
  [key: string]: string | undefined;
}

/**
 * Project home — header with project metadata, 3 stat tiles, and a
 * project-scoped run feed. Run feed uses the same AG-Grid component
 * as the tenant-wide /runs page but with the Project column hidden.
 */
export function ProjectDetailPage(): JSX.Element | null {
  const { slug = "" } = useParams<RouteParams>();
  const project = useProject(slug);
  const runs = useProjectRuns(slug);

  if (project.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-5 w-1/4" />
        <Skeleton className="h-9 w-1/2" />
        <Skeleton className="h-5 w-1/3" />
        <div className="grid grid-cols-3 gap-4 sm:max-w-md">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
        <Skeleton className="h-[560px] w-full" />
      </div>
    );
  }

  if (project.isError) {
    const isNotFound = project.error instanceof ApiError && project.error.status === 404;
    return (
      <EmptyState
        title={isNotFound ? "Project not found." : "Could not load project."}
        description={
          isNotFound
            ? `No project with slug ${slug ? `'${slug}'` : "(empty)"}.`
            : project.error instanceof Error
              ? project.error.message
              : "The FlowStack server returned an error."
        }
      />
    );
  }

  if (!project.data) return null;

  const breadcrumbs = (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Link to="/projects" className="hover:text-foreground">
        Projects
      </Link>
      <span aria-hidden>·</span>
      <span className="font-mono">{project.data.slug}</span>
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      {breadcrumbs}
      <PageHeader
        title={project.data.display_name}
        description={project.data.description ?? undefined}
        actions={<ProjectActions slug={project.data.slug} />}
      />

      <div className="grid grid-cols-3 gap-4 sm:max-w-md">
        <StatTile label="Runs" value={project.data.run_count} />
        <StatTile label="Active" value={project.data.active_run_count} />
        <StatTile label="Artifacts" value={project.data.artifact_count} />
      </div>

      <FlowLaunchPanel slug={project.data.slug} />

      <section aria-labelledby="runs-heading" className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-2">
          <h2 id="runs-heading" className="font-display text-xl font-semibold tracking-tight">
            Runs
          </h2>
          {/* fs#1797: this feed is the project's most recent runs; the full,
              searchable feed is the Runs page. */}
          <Link to="/runs" className="text-xs text-muted-foreground hover:text-foreground">
            All runs
          </Link>
        </div>
        {runs.isLoading ? (
          <Skeleton className="h-[560px] w-full" />
        ) : runs.isError ? (
          <EmptyState
            title="Could not load runs."
            description={runs.error instanceof Error ? runs.error.message : "Unknown error."}
          />
        ) : !runs.data || runs.data.length === 0 ? (
          <EmptyState
            title="No runs yet."
            description="Pick a Flow template above to run an AFFDO pipeline, or submit a Tile from the SDK."
            cta={
              <Link to={flowSubmitPath(project.data.slug)} className={buttonVariants()}>
                <Play className="mr-2 h-4 w-4" aria-hidden />
                Run an AFFDO pipeline
              </Link>
            }
          />
        ) : (
          <RunListAgGrid runs={runs.data} showProjectColumn={false} />
        )}
      </section>
    </div>
  );
}

function StatTile({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <Card className="p-card">
      <p className="label-micro text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl font-semibold tabular-nums">{value}</p>
    </Card>
  );
}
